// Pure client-side catalog engine: filtering, faceting (with smart graying), search,
// sorting and paging. No React, no DOM - so it can be unit-validated against the real
// sample data with plain Node. This is the ported publixx offline-catalog logic.

import {
    compactForms,
    foldWord,
    FuzzyVocabulary,
    hasWord,
    queryWords,
    splitWords,
    squash,
    squashedForm,
    type QueryWord,
} from './fuzzySearch';
import {
    loc,
    nodeLabel,
    type ActiveFilters,
    type AttributeMapping,
    type AttributeValueType,
    type CatalogConfig,
    type CatalogLabels,
    type ComputedFacet,
    type DateRangeValue,
    type DocumentIndexItem,
    type FacetDef,
    type IndexItem,
    type MediaItem,
    type RangeValue,
    type SearchConfig,
    type Tree,
    type TreeNode,
} from './types';

export interface SortState {
    key: string;
    dir: 'asc' | 'desc';
}

export interface CatalogQueryState {
    /** The tree node being BROWSED. Ignored while a search runs: a search always covers the whole
     * (scope-cut) catalog - the former Suchbereich switch between "this node" and "Ganzer Katalog"
     * confused more than it helped (reported) and is gone. */
    selectedNodeId?: string;
    search: string;
    filters: ActiveFilters;
    sort: SortState;
    page: number;
    perPage: number;
    columnFilters?: Record<string, string>;
    treeIndex: TreeIndex;
    categoryLabels?: CategoryLabels;
    // artNr -> its position in the currently selected system node's own articleNumbers array
    // (the curated STIBO delivery order, same order SystemDetailPage's Artikelliste tab shows) -
    // only set while a single system (tree leaf) is selected; consulted when sort.key === 'natural'
    // (see sortItems' fallback below), ignored otherwise.
    naturalOrder?: Map<string, number>;
    /**
     * The ranked hits of `search` over the very `index` handed to selectCatalog, for a caller that
     * already computed them (CatalogPage needs the same set for the navigation badges). Saves the
     * second search, which is a full pass over the catalog - and, for a query the exact search
     * misses, a second token search on top. Anything else must leave it unset.
     */
    searchHits?: IndexItem[];
}

export interface CatalogResult {
    items: IndexItem[];
    total: number;
    facets: ComputedFacet[];
    searching: boolean;
    /** Distinct Kategorie values available to that column's filter dropdown (see
     * distinctColumnValues) - computed from the matched set with every OTHER column filter
     * applied but the category filter itself excluded, so picking a category never collapses
     * its own option list to the one value just picked. */
    categoryOptions: string[];
}

/** Lowercases, strips diacritics (ä -> a) and trims - the shared normalization for every
 * substring/typo match in this module; also used directly by simple list-local search boxes
 * (e.g. FacetGroup's option search) that don't need the full scored searchScore. A list of ARTICLES
 * narrowed by a typed query goes through filterByText instead, which adds the token stage. */
/**
 * One collator per locale instead of one per comparison.
 *
 * `String.prototype.localeCompare` builds a collator internally on EVERY call - across a sort of a
 * five-figure list that is well over a million throwaway objects and the single most expensive line
 * in the whole filter path. `Intl.Collator#compare`, created once and reused, is the same
 * comparison an order of magnitude faster.
 *
 * `numeric: true` throughout, which is also the more correct ordering for this data: article names
 * and numbers sort naturally ("Träger 9" before "Träger 10"), where a plain lexicographic compare
 * put 10 first. sortItems already asked for it; the other call sites now get it too.
 */
const collators = new Map<string, Intl.Collator>();

const collatorFor = (locale?: string): Intl.Collator => {
    const key = locale ?? '';
    let collator = collators.get(key);
    if (!collator) {
        collator = new Intl.Collator(locale || undefined, { numeric: true });
        collators.set(key, collator);
    }
    return collator;
};

/** Locale-aware, natural-order string comparison - the shared comparator for every sort in this
 * module (and for the option lists the UI builds, see ProductGrid/SearchMask). */
export const compareText = (a: string, b: string, locale?: string): number => collatorFor(locale).compare(a, b);

export const normalize = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const asArray = (raw: unknown): string[] => {
    if (raw == null) {
        return [];
    }
    return (Array.isArray(raw) ? raw : [raw]).map((v) => String(v));
};

const toNumber = (raw: unknown): number => (typeof raw === 'number' ? raw : Number(raw));

export const byKey = (defs: FacetDef[]): Record<string, FacetDef> =>
    Object.fromEntries(defs.map((d) => [d.key, d]));

// ---- cross-page-navigation memoization ------------------------------------------------
// Every catalog page (SearchMask, CatalogPage, ArticleDetailPage, SystemDetailContent,
// WishlistPage) independently builds the same handful of lookup structures (tree index, artNr
// lookup, document language/docType option lists) from the same underlying arrays - the ones
// TanStack Query hands out for a given query key, stable by reference for as long as that query
// stays cached (see queries.ts, staleTime 5 Min for most of these). Each page already wraps its
// own call in its own useMemo, so this is NOT about deduping within one page's render - it's
// about NAVIGATING between pages: mount CatalogPage (computes once), navigate to an article's
// Dokumente tab (a brand-new component, a brand-new useMemo with no memory of CatalogPage's
// identical computation) - without caching keyed on the array's own identity, real (sometimes
// O(n) over thousands of rows) work would redo itself from scratch on every single page that
// needs it, even though the underlying array never changed.
//
// memoizeByRef wraps that one-key-in, one-value-out shape once instead of hand-rolling the same
// WeakMap get-or-compute block per function. distinctDocumentLanguages below doesn't use it
// directly - it has a second (locale) dimension a single WeakMap can't key on - but follows the
// exact same "check the map, compute once, cache forever (until GC'd when the array itself is no
// longer referenced anywhere)" shape.
const memoizeByRef = <TArg extends object, TResult>(compute: (arg: TArg) => TResult): ((arg: TArg) => TResult) => {
    const cache = new WeakMap<TArg, TResult>();
    return (arg: TArg): TResult => {
        // has()-then-get(), not a truthy check on get()'s result: every current compute() happens
        // to return a Map/array (always truthy), but a falsy-but-real result (0, '', false) from a
        // future caller would otherwise recompute on every single call, silently defeating the
        // cache with no error to point at why.
        if (cache.has(arg)) {
            return cache.get(arg) as TResult;
        }
        const result = compute(arg);
        cache.set(arg, result);
        return result;
    };
};

// ---- tree (portfolio/group/family/system) index -------------------------------------
// Articles carry system-level (leaf) nodeIds, documents may also target a node directly. A node
// "contains" such a target iff the target IS that node or lies below it - which every consumer
// (isInSubtree, categoryCounts, documentCategoryCounts) answers from the ancestor chain we
// precompute here, so counting and filtering can never disagree on what a subtree contains.

export interface TreeIndexEntry {
    node: TreeNode;
    ancestorIds: string[];
}

export type TreeIndex = Map<string, TreeIndexEntry>;

// Walks the whole tree (recursive) - real work, and per memoizeByRef's own comment above, called
// against the SAME `tree` array reference by every catalog page.
export const buildTreeIndex = memoizeByRef((tree: Tree): TreeIndex => {
    const index: TreeIndex = new Map();
    const walk = (nodes: TreeNode[], ancestorIds: string[]): void => {
        for (const node of nodes) {
            index.set(node.nodeId, { node, ancestorIds });
            walk(node.children, [...ancestorIds, node.nodeId]);
        }
    };
    walk(tree, []);
    return index;
});

/** artNr -> IndexItem lookup, e.g. for resolving a document's owning article's name/nodeIds. */
export const buildIndexByArtNr = memoizeByRef((index: IndexItem[]): Map<string, IndexItem> =>
    new Map(index.map((item) => [item.artNr, item]))
);

// A well-formed tree only ever tags true leaves as 'system' (see types.ts's TreeNode.articleNumbers
// comment), but real STIBO deliveries aren't guaranteed well-formed - checking children.length here
// too (not just the declared type) keeps "is this a system leaf" from misfiring on a node that's
// mistagged 'system' while still having children of its own (see chat: linking to the system page
// happened a level too early).
export const isSystemLeaf = (node: TreeNode): boolean => node.type === 'system' && node.children.length === 0;

export interface BreadcrumbEntry {
    nodeId: string;
    label: string;
}

/** Ancestor chain (root -> selected), localized, for breadcrumb rendering. */
export const breadcrumbForNodeId = (
    nodeId: string | undefined,
    treeIndex: TreeIndex,
    locale: string
): BreadcrumbEntry[] => {
    if (!nodeId) {
        return [];
    }
    const entry = treeIndex.get(nodeId);
    if (!entry) {
        return [];
    }
    return [...entry.ancestorIds, nodeId].map((id) => ({
        nodeId: id,
        label: nodeLabel(treeIndex.get(id)?.node, locale),
    }));
};

// ---- tree search -----------------------------------------------------------------------------
//
// The free-text search covers articles only (config.search.fields). The customer also wants the
// TREE searched - "framax" should surface the system, family and group nodes named like that,
// listed by level below the article hits (see chat). Deliberately NOT folded into the article
// hits (articles under a matching node do not become hits themselves): that would inflate the
// count and drown the relevance order; clicking a node hit navigates into it instead.

export interface TreeSearchHit {
    nodeId: string;
    label: string;
    /** Ancestor labels, root first ("Systeme › Decken-Systeme") - context for same-named nodes. */
    path: string;
}

export interface TreeSearchHits {
    system: TreeSearchHit[];
    family: TreeSearchHit[];
    group: TreeSearchHit[];
}

const EMPTY_TREE_HITS: TreeSearchHits = { system: [], family: [], group: [] };

/** Nodes whose localized name contains the query (same normalization as the article search),
 * bucketed by level and sorted by label. `withinNodeIds` restricts to those subtrees (the landing
 * page's multi-select scope or the results page's node-scoped search, see inAnyCategory); empty =
 * whole tree. `counts` (categoryCounts of the products currently in play) drops nodes with nothing
 * under them - the navigation tree hides those too, and a hit that lands on "Keine passenden
 * Artikel" is a dead end. */
export const searchTree = (
    treeIndex: TreeIndex,
    search: string,
    locale: string,
    withinNodeIds: string[] = [],
    counts?: Record<string, number>
): TreeSearchHits => {
    const query = normalize(search);
    if (!query) {
        return EMPTY_TREE_HITS;
    }
    const hits: TreeSearchHits = { system: [], family: [], group: [] };
    for (const entry of treeIndex.values()) {
        const bucket = hits[entry.node.type as keyof TreeSearchHits];
        if (!bucket) {
            continue;
        }
        if (withinNodeIds.length > 0 && !withinNodeIds.some((scope) => isInSubtree(entry.node.nodeId, scope, treeIndex))) {
            continue;
        }
        if (counts && (counts[entry.node.nodeId] ?? 0) === 0) {
            continue;
        }
        const label = nodeLabel(entry.node, locale);
        if (!label || !normalize(label).includes(query)) {
            continue;
        }
        bucket.push({
            nodeId: entry.node.nodeId,
            label,
            path: entry.ancestorIds.map((id) => nodeLabel(treeIndex.get(id)?.node, locale)).join(' › '),
        });
    }
    (Object.keys(hits) as (keyof TreeSearchHits)[]).forEach((level) =>
        hits[level].sort((a, b) => compareText(a.label, b.label, locale))
    );
    return hits;
};

/** Breadcrumb label per article (keyed by artNr), resolved from its first nodeId. */
/**
 * Breadcrumb label per article ("Systeme › Decken-Systeme › …"), resolved ON DEMAND.
 *
 * A `Map` satisfies this interface, which is what keeps every consumer (ProductGrid, sortItems,
 * applyColumnFilters, …) unchanged - they only ever call `.get()`.
 */
export interface CategoryLabels {
    get(artNr: string): string | undefined;
}

/**
 * Two levels of laziness, because this used to be the most expensive thing the catalog did on a
 * language switch:
 *
 * 1. Nothing is built up front except an artNr -> nodeId lookup. Building every article's label
 *    eagerly meant walking the tree and joining a string for a five-figure number of articles, of
 *    which a page shows fifty - and it was redone whenever the locale changed.
 * 2. The breadcrumb itself is cached per NODE, not per article. Articles of the same system share
 *    one breadcrumb, so even a full pass (sorting or filtering by Kategorie, which does touch every
 *    row) resolves as many breadcrumbs as there are systems, not as there are articles.
 *
 * Cache lifetime is the returned object's: callers memoize it per index/tree/locale, so a language
 * switch produces a fresh, empty one - no stale label can survive.
 */
const categoryLabelCache = new WeakMap<IndexItem[], WeakMap<TreeIndex, Map<string, CategoryLabels>>>();

/**
 * The lookup below, shared per index/tree/locale across every caller and every mount. A system
 * view (SystemDetailContent) builds it on each click on a system - with the full catalog that
 * was a pass over 119,000 index rows, 40 ms of the click (DevTools trace). Same lifetime rule as
 * memoizeByRef above: a new index, tree or locale gets a fresh, empty lookup.
 */
export const buildCategoryLabels = (index: IndexItem[], treeIndex: TreeIndex, locale: string): CategoryLabels => {
    let byTree = categoryLabelCache.get(index);
    if (!byTree) {
        byTree = new WeakMap();
        categoryLabelCache.set(index, byTree);
    }
    let byLocale = byTree.get(treeIndex);
    if (!byLocale) {
        byLocale = new Map();
        byTree.set(treeIndex, byLocale);
    }
    let labels = byLocale.get(locale);
    if (!labels) {
        labels = createCategoryLabels(index, treeIndex, locale);
        byLocale.set(locale, labels);
    }
    return labels;
};

const createCategoryLabels = (index: IndexItem[], treeIndex: TreeIndex, locale: string): CategoryLabels => {
    const nodeIdByArtNr = new Map<string, string | undefined>();
    for (const item of index) {
        nodeIdByArtNr.set(item.artNr, item.nodeIds?.[0]);
    }
    const labelByNodeId = new Map<string, string>();
    const labelByArtNr = new Map<string, string>();

    return {
        get(artNr: string): string | undefined {
            const cached = labelByArtNr.get(artNr);
            if (cached !== undefined) {
                return cached;
            }
            if (!nodeIdByArtNr.has(artNr)) {
                return undefined;
            }
            const nodeId = nodeIdByArtNr.get(artNr);
            const nodeKey = nodeId ?? '';
            let label = labelByNodeId.get(nodeKey);
            if (label === undefined) {
                label = breadcrumbForNodeId(nodeId, treeIndex, locale)
                    .map((entry) => entry.label)
                    .join(' › ');
                labelByNodeId.set(nodeKey, label);
            }
            labelByArtNr.set(artNr, label);
            return label;
        }
    };
};

/**
 * True if `id` IS `nodeId` or sits anywhere below it - the membership test behind every "on or
 * below the selected node" filter, and the exact mirror of how categoryCounts /
 * documentCategoryCounts roll a target up the tree ([...ancestorIds, id]).
 *
 * Deliberately not `treeIndex.get(nodeId).systemIds.has(id)`: that set holds only the 'system'
 * leaves of a subtree, so a target attached to a non-leaf node (a mistagged delivery, or a
 * documents.json row pointing at a group/family directly) rolled up into the tree BADGE while the
 * list filter dropped it - a node advertising documents whose list then came up empty.
 */
export const isInSubtree = (id: string, nodeId: string, treeIndex: TreeIndex): boolean =>
    id === nodeId || (treeIndex.get(id)?.ancestorIds.includes(nodeId) ?? false);

/** True if the item lies on or below the selected tree node. */
export const inCategory = (item: IndexItem, nodeId: string | undefined, treeIndex: TreeIndex): boolean => {
    if (!nodeId) {
        return true;
    }
    if (!treeIndex.has(nodeId)) {
        return false;
    }
    return (item.nodeIds ?? []).some((id) => isInSubtree(id, nodeId, treeIndex));
};

/**
 * Searchable text per item and field, computed once and remembered: the normalized text for the
 * exact search and - built the first time the token search needs it - the compact forms for that
 * one (see fuzzySearch.compactForms).
 *
 * `normalize` is the expensive part of a search (lowercase + NFD decomposition + a diacritics
 * regex), and without this cache the search ran it for EVERY field of EVERY item on EVERY
 * keystroke - in the order of half a million calls per typed character across a five-figure
 * catalog, which is what made typing feel sticky.
 *
 * A WeakMap keyed by the item object rather than by article number: the index array is replaced
 * wholesale whenever it is refetched (see provider.ts's resolveIndex* functions, which map to fresh
 * objects), so a new delivery brings new keys and the old entries become collectable by themselves -
 * there is no cache to invalidate and no way to serve a stale value. Both forms share ONE map per
 * item (the compact one under a prefixed key): at catalog scale it is the per-item overhead, not the
 * strings, that dominates the memory this costs.
 */
const fieldTexts = new WeakMap<IndexItem, Map<string, string>>();

// Prefix of the compact-forms entry of a field - a character no field key contains.
const COMPACT_KEY = '\u0001';

const itemTexts = (item: IndexItem): Map<string, string> => {
    let perItem = fieldTexts.get(item);
    if (!perItem) {
        perItem = new Map();
        fieldTexts.set(item, perItem);
    }
    return perItem;
};

const normalizedField = (item: IndexItem, key: string): string => {
    const perItem = itemTexts(item);
    const cached = perItem.get(key);
    if (cached !== undefined) {
        return cached;
    }
    const raw = item[key];
    const value = raw == null ? '' : normalize(Array.isArray(raw) ? raw.join(' ') : String(raw));
    perItem.set(key, value);
    return value;
};

const compactField = (item: IndexItem, key: string): string => {
    const perItem = itemTexts(item);
    const cached = perItem.get(COMPACT_KEY + key);
    if (cached !== undefined) {
        return cached;
    }
    const value = compactForms(normalizedField(item, key));
    perItem.set(COMPACT_KEY + key, value);
    return value;
};

/**
 * Whether a query is one complete UUID - the only shape that reaches the id fields below.
 *
 * Deliberately not a fragment or a general "looks technical" rule: these ids are cryptic keys that
 * are pasted whole (out of a URL, a failing request, a blob listing), never typed, so a partial
 * match buys nothing - and it would cost. Ids are compared with a plain `includes`, and a UUID
 * contains every short digit sequence somewhere: a "40" or a "586214" would otherwise start
 * dragging in unrelated articles. A full UUID cannot collide with anything by accident, so the
 * normal search behaves exactly as before.
 *
 * `normalize` has already lowercased and trimmed the query, so a UUID pasted in upper case works.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const isTechnicalIdQuery = (normalizedQuery: string): boolean => UUID_PATTERN.test(normalizedQuery);

/**
 * Id-carrying fields searched IN ADDITION to the configured search fields, for a full-UUID query.
 * Not part of catalog-config.json's `search.fields` on purpose: those are the customer-facing,
 * weighted text fields, while these exist so a UUID out of a URL, a network-tab 404 or a blob
 * listing can be pasted into the search box and lead straight to the article it belongs to.
 */
const ID_FIELDS = ['imageAssetId', 'nodeIds'] as const;

// Deliberately low: an id hit should surface the article, never outrank a real name/artNr match.
const ID_FIELD_WEIGHT = 1;

/** The actual scoring, against an ALREADY normalized query - normalizing it inside the per-item
 * loop meant redoing the same work once per item (see searchScore below, which is the entry point
 * for single-item callers). */
const scoreNormalized = (item: IndexItem, normalizedQuery: string, config: SearchConfig): number => {
    let score = 0;
    for (const field of config.fields) {
        if (item[field.key] == null) {
            continue;
        }
        const text = normalizedField(item, field.key);
        const hit =
            field.pattern === 'prefix'
                ? text.startsWith(normalizedQuery)
                : field.pattern === 'exact'
                  ? text === normalizedQuery
                  : text.includes(normalizedQuery);
        if (hit) {
            score += field.weight;
        }
    }
    if (isTechnicalIdQuery(normalizedQuery)) {
        for (const key of ID_FIELDS) {
            if (item[key] != null && normalizedField(item, key).includes(normalizedQuery)) {
                score += ID_FIELD_WEIGHT;
            }
        }
    }
    return score;
};

export const searchScore = (item: IndexItem, query: string, config: SearchConfig): number =>
    scoreNormalized(item, normalize(query), config);

/** Orders by name, articles without one last (as sortItems does - not among the N names, which
 * is where the string "null" would put them). */
const compareByName = (a: IndexItem, b: IndexItem): number => {
    const aEmpty = !a.name;
    const bEmpty = !b.name;
    if (aEmpty !== bEmpty) {
        return aEmpty ? 1 : -1;
    }
    return compareText(a.name ?? '', b.name ?? '');
};

/** Drops non-hits (and parts, unless configured in), orders by score, then by name. */
const rankHits = (config: CatalogConfig, scored: { item: IndexItem; score: number }[]): IndexItem[] =>
    scored
        .filter((x) => x.score > 0 && (config.search.includeParts || x.item.type === 'product'))
        .sort((a, b) => b.score - a.score || compareByName(a.item, b.item))
        .map((x) => x.item);

/**
 * Stage 2 of the search: the fallback for a query the exact search found nothing for.
 *
 * The query is split into words and every word must occur SOMEWHERE in the item (AND, any field),
 * compared on the squashed/folded text forms (see fuzzySearch.compactForms) - so "stahl beton"
 * finds "Stahlbeton" and "Stahl-Beton-Wand", "beton stahl" finds both too, "stahlbeton" finds
 * "Stahl-Beton", and "traeger" finds "Träger". Fields with a prefix/exact pattern (article numbers)
 * cannot be split into words; they are matched with the whole squashed query instead, which lets
 * "588 000" find article number "588000..".
 *
 * Only runs when stage 1 came up empty, so a query that works today ranks exactly as before.
 */
const scoreTokens = (item: IndexItem, words: QueryWord[], joined: string, config: SearchConfig): number => {
    let wordScore = 0;
    let allWordsFound = words.length > 0;
    for (const word of words) {
        let best = 0;
        for (const field of config.fields) {
            if (field.pattern !== 'contains' || item[field.key] == null) {
                continue;
            }
            if (field.weight > best && hasWord(compactField(item, field.key), word)) {
                best = field.weight;
            }
        }
        if (best === 0) {
            // One word missing and the item is out (AND) - the rest is not worth checking. Most
            // articles fail on the first word, so this spares them the remaining words' scan.
            allWordsFound = false;
            break;
        }
        wordScore += best;
    }
    let score = allWordsFound ? wordScore : 0;
    for (const field of config.fields) {
        if (field.pattern === 'contains' || item[field.key] == null) {
            continue;
        }
        const text = squashedForm(compactField(item, field.key));
        if (field.pattern === 'prefix' ? text.startsWith(joined) : text === joined) {
            score += field.weight;
        }
    }
    return score;
};

const searchTokens = (index: IndexItem[], config: CatalogConfig, search: string): IndexItem[] => {
    const normalizedQuery = normalize(search);
    const words = queryWords(normalizedQuery);
    const joined = squash(normalizedQuery);
    if (joined.length < 2) {
        return [];
    }
    return rankHits(
        config,
        index.map((item) => ({ item, score: scoreTokens(item, words, joined, config.search) }))
    );
};

/**
 * The articles of a list that match a free-text query by name or article number - searchIndex's
 * counterpart for views that narrow a list they already hold (a system's Artikelliste), where the
 * catalog-wide ranking, description fields and article-number-prefix rule would change what that
 * box has always matched. Same two stages though, so the one search box behaves alike wherever it
 * is typed into: the plain substring match decides alone when it finds anything, the token match
 * (see scoreTokens) only steps in for a query that found nothing. Keeps the list's own order.
 */
export const filterByText = (items: IndexItem[], query: string): IndexItem[] => {
    const q = normalize(query);
    if (!q) {
        return items;
    }
    const exact = items.filter((item) => normalize(item.name ?? '').includes(q) || normalize(item.artNr).includes(q));
    if (exact.length > 0) {
        return exact;
    }
    const words = queryWords(q);
    const joined = squash(q);
    if (joined.length < 2) {
        return exact;
    }
    return items.filter((item) => {
        const name = compactForms(normalize(item.name ?? ''));
        const artNr = compactForms(normalize(item.artNr));
        return (
            (words.length > 0 && words.every((word) => hasWord(name, word) || hasWord(artNr, word))) ||
            squashedForm(name).includes(joined) ||
            squashedForm(artNr).includes(joined)
        );
    });
};

export const applyFilters = (
    items: IndexItem[],
    filters: ActiveFilters,
    defs: Record<string, FacetDef>
): IndexItem[] => {
    const active = Object.entries(filters);
    // Boolean facets marked hideByDefault (e.g. "archived") exclude true-valued items whenever
    // their own filter isn't explicitly set - lets a facet default to hidden without a separate
    // "default filters" concept, and without disturbing the Smart Graying pattern below (a
    // facet's own count computation removes its def from `defs`, see computeFacets).
    const hiddenByDefault = Object.values(defs).filter(
        (def) => def.type === 'boolean' && def.hideByDefault && !(def.key in filters)
    );
    if (active.length === 0 && hiddenByDefault.length === 0) {
        return items;
    }
    return items.filter((item) => {
        if (hiddenByDefault.some((def) => item[def.key] === true)) {
            return false;
        }
        return active.every(([key, value]) => {
            const def = defs[key];
            if (!def) {
                return true;
            }
            const raw = item[key];
            if (def.type === 'value-list') {
                const selected = value as string[];
                if (!selected.length) {
                    return true;
                }
                const values = asArray(raw);
                return selected.some((s) => values.includes(s));
            }
            if (def.type === 'range') {
                const { min, max } = value as RangeValue;
                const n = toNumber(raw);
                if (Number.isNaN(n)) {
                    return false;
                }
                return (min == null || n >= min) && (max == null || n <= max);
            }
            if (def.type === 'date-range') {
                const { from, to } = value as DateRangeValue;
                if (typeof raw !== 'string' || !raw) {
                    return false;
                }
                // ISO "YYYY-MM-DD" sorts correctly under plain string comparison - no Date
                // parsing needed, keeps this engine dependency-free and pure.
                return (!from || raw >= from) && (!to || raw <= to);
            }
            if (def.type === 'boolean') {
                return value !== true || raw === true;
            }
            return true;
        });
    });
};

/**
 * Smart graying: each facet is counted over the base set with all OTHER active filters
 * applied (its own filter excluded). Values present in the base but absent from the
 * filtered subset are returned with count 0 (grayed), never removed.
 */
export const computeFacets = (
    base: IndexItem[],
    filters: ActiveFilters,
    defs: FacetDef[]
): ComputedFacet[] => {
    const map = byKey(defs);
    return defs.map((def) => {
        const others: ActiveFilters = { ...filters };
        delete others[def.key];
        // Also drop this facet's own def so a hideByDefault rule (see applyFilters) doesn't
        // suppress its OWN count - e.g. archived's "Ja (N)" label must reflect how many archived
        // items exist, not 0, even though archived items stay hidden from every OTHER facet's
        // count and from the final filtered result.
        const othersDefs: Record<string, FacetDef> = { ...map };
        delete othersDefs[def.key];
        const subset = applyFilters(base, others, othersDefs);

        if (def.type === 'value-list') {
            const domain = new Set<string>();
            base.forEach((i) => asArray(i[def.key]).forEach((v) => domain.add(v)));
            const counts = new Map<string, number>();
            subset.forEach((i) => asArray(i[def.key]).forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1)));
            const values = [...domain]
                .map((value) => ({ value, count: counts.get(value) ?? 0 }))
                .sort((a, b) => b.count - a.count || compareText(a.value, b.value));
            return { ...def, values };
        }

        if (def.type === 'range') {
            const nums = subset.map((i) => toNumber(i[def.key])).filter((n) => !Number.isNaN(n));
            return { ...def, bounds: nums.length ? { min: Math.min(...nums), max: Math.max(...nums) } : undefined };
        }

        if (def.type === 'date-range') {
            const dates = subset.map((i) => i[def.key]).filter((v): v is string => typeof v === 'string' && v.length > 0);
            return {
                ...def,
                dateBounds: dates.length ? { min: dates.reduce((a, b) => (a < b ? a : b)), max: dates.reduce((a, b) => (a > b ? a : b)) } : undefined,
            };
        }

        const truthy = subset.filter((i) => i[def.key] === true).length;
        return { ...def, counts: { true: truthy, false: subset.length - truthy } };
    });
};

/** Display value of a list column for an item (used by the Excel-style column filters). */
export const columnValue = (item: IndexItem, key: string, categoryLabels?: CategoryLabels): string => {
    if (key === 'name') {
        // An article without a name in this language is delivered with name = null (see sortItems);
        // normalize() on that would throw and take the whole list down with the column filter.
        return item.name ?? '';
    }
    if (key === 'artNr') {
        return item.artNr;
    }
    if (key === 'category') {
        return categoryLabels?.get(item.artNr) ?? '';
    }
    const raw = item[key];
    if (Array.isArray(raw)) {
        return raw.join(', ');
    }
    if (typeof raw === 'boolean') {
        return raw ? 'Ja' : 'Nein';
    }
    return raw == null ? '' : String(raw);
};

/** Per-column free-text "contains" filter (Excel-style), reduces the list before paging. */
export const applyColumnFilters = (
    items: IndexItem[],
    filters: Record<string, string>,
    categoryLabels?: CategoryLabels
): IndexItem[] => {
    const active = Object.entries(filters).filter(([, text]) => text && text.trim());
    if (active.length === 0) {
        return items;
    }
    return active.reduce(
        (rows, [key, text]) => {
            const needle = normalize(text);
            return rows.filter((item) => normalize(columnValue(item, key, categoryLabels)).includes(needle));
        },
        items
    );
};

/**
 * Distinct, sorted values of one column across `items` - the option list for a column filter that
 * offers a dropdown of real values instead of free text (see ProductGrid's Kategorie column, which
 * has a small bounded value set the user would otherwise have to know by heart and type exactly).
 * Empty values are dropped: "no category" is what the unfiltered list already shows.
 */
export const distinctColumnValues = (
    items: IndexItem[],
    key: string,
    categoryLabels?: CategoryLabels
): string[] =>
    [...new Set(items.map((item) => columnValue(item, key, categoryLabels)).filter(Boolean))].sort(compareText);

export const sortItems = (
    items: IndexItem[],
    sort: SortState,
    categoryLabels?: CategoryLabels
): IndexItem[] => {
    const factor = sort.dir === 'desc' ? -1 : 1;
    // "category" isn't a real IndexItem field (it's a resolved tree breadcrumb, see
    // columnValue) - sort by the same resolved label the column itself displays.
    const valueOf = (item: IndexItem) => (sort.key === 'category' ? categoryLabels?.get(item.artNr) : item[sort.key]);
    return [...items].sort((a, b) => {
        const av = valueOf(a);
        const bv = valueOf(b);
        // Missing values last, whichever direction: an article without a name in this language
        // (see the converter's GetLocalizedOrFallback for the root cause) used to sort to the very
        // top of a name-sorted list as an empty string - a row with a picture, a number and no name.
        const aEmpty = av == null || av === '';
        const bEmpty = bv == null || bv === '';
        if (aEmpty !== bEmpty) {
            return aEmpty ? 1 : -1;
        }
        if (typeof av === 'number' && typeof bv === 'number') {
            return (av - bv) * factor;
        }
        return compareText(String(av ?? ''), String(bv ?? '')) * factor;
    });
};

/** Orders items by their position in a system node's own articleNumbers array (the curated STIBO
 * delivery order) - items with no entry (shouldn't normally happen for a properly scoped list)
 * sort after everything that does. */
const sortByNaturalOrder = (items: IndexItem[], order: Map<string, number>): IndexItem[] =>
    [...items].sort(
        (a, b) => (order.get(a.artNr) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.artNr) ?? Number.MAX_SAFE_INTEGER)
    );

/**
 * The ranked hits of a free-text query across the WHOLE catalog - no category, no facets, parts
 * included per config.search.includeParts.
 *
 * Exported so a caller that needs the same set elsewhere can compute it once and hand it to
 * selectCatalog (CatalogQueryState.searchHits).
 */
export const searchIndex = (index: IndexItem[], config: CatalogConfig, search: string): IndexItem[] => {
    // Normalized once here, not once per item (see scoreNormalized).
    const normalizedQuery = normalize(search);
    const exact = rankHits(
        config,
        index.map((item) => ({ item, score: scoreNormalized(item, normalizedQuery, config.search) }))
    );
    // Multi-stage: the exact search decides alone whenever it finds anything. Only an empty result
    // falls through to the token search - so the tolerant stage can never reorder or dilute hits
    // the exact one would have shown, and costs nothing on the (usual) successful query.
    return exact.length > 0 ? exact : searchTokens(index, config, search);
};

/**
 * Facet defs for filtering SEARCH hits: the same filters, minus the hideByDefault rules (see
 * applyFilters). A search has always found e.g. archived articles - typing an article number is
 * how one looks up an old item - and an explicit filter must not quietly change that; an
 * explicitly SET archived filter still applies as usual.
 */
const searchFacetDefs = (facets: FacetDef[]): FacetDef[] =>
    facets.map((def) => (def.hideByDefault ? { ...def, hideByDefault: false } : def));

/**
 * Search hits narrowed by the active facet filters. A search used to CLEAR the filters (the old
 * publixx rule) - which read as "my filters keep disappearing" (reported: "Die Filter
 * verschwinden ab und zu ... wenn man einen Suchbegriff eingegeben hat"), and a landing-page
 * assortment like "Weltweites Sortiment" stood in the filter badge while the hits ignored it.
 * Filters now stay put and narrow the hits, like any shop search.
 */
const filterSearchHits = (hits: IndexItem[], filters: ActiveFilters, facets: FacetDef[]): IndexItem[] =>
    Object.keys(filters).length > 0 ? applyFilters(hits, filters, byKey(searchFacetDefs(facets))) : hits;

/** Single entry point used by the UI. Neither a search nor a category selection clears the
 * filters; a search ignores the category selection and covers the whole catalog. */
export const selectCatalog = (
    index: IndexItem[],
    config: CatalogConfig,
    state: CatalogQueryState
): CatalogResult => {
    const searching = state.search.trim().length >= config.search.minChars;

    let matched: IndexItem[];
    let facets: ComputedFacet[] = [];

    if (searching) {
        // A search ranks the whole catalog and may surface parts - the tree selection plays no
        // part in it (see CatalogQueryState.selectedNodeId); only the visible filters narrow it.
        const hits = state.searchHits ?? searchIndex(index, config, state.search);
        // The facet filters narrow the hits (see filterSearchHits), and the facets are computed
        // over the hits - so the filter drawer stays available during a search and offers exactly
        // what occurs among them. Rank order is kept: no re-sort.
        facets = computeFacets(hits, state.filters, searchFacetDefs(config.facets));
        matched = filterSearchHits(hits, state.filters, config.facets);
    } else {
        const base = index.filter(
            (i) => i.type === 'product' && inCategory(i, state.selectedNodeId, state.treeIndex)
        );
        facets = computeFacets(base, state.filters, config.facets);
        const filtered = applyFilters(base, state.filters, byKey(config.facets));
        matched =
            state.sort.key === 'natural' && state.naturalOrder
                ? sortByNaturalOrder(filtered, state.naturalOrder)
                : sortItems(
                      filtered,
                      state.sort.key === 'natural' ? { key: 'name', dir: 'asc' } : state.sort,
                      state.categoryLabels
                  );
    }

    // Kategorie dropdown options: every OTHER column filter applied, the category filter itself
    // excluded - same "a group's own filter never shrinks its own option list" rule the facet
    // panels follow (see computeDocumentFacets / doka-pih principle 3). Computed before the
    // category filter narrows `matched` below.
    const otherColumnFilters = Object.fromEntries(
        Object.entries(state.columnFilters ?? {}).filter(([key]) => key !== 'category')
    );
    const categoryOptions = distinctColumnValues(
        applyColumnFilters(matched, otherColumnFilters, state.categoryLabels),
        'category',
        state.categoryLabels
    );

    if (state.columnFilters) {
        matched = applyColumnFilters(matched, state.columnFilters, state.categoryLabels);
    }

    const total = matched.length;
    // Clamp the page so a stale, too-high page never slices an empty window after the result shrank.
    const pageCount = Math.max(1, Math.ceil(total / state.perPage));
    const safePage = Math.min(Math.max(1, state.page), pageCount);
    const start = (safePage - 1) * state.perPage;
    return { items: matched.slice(start, start + state.perPage), total, facets, searching, categoryOptions };
};

/** Product counts per tree node id (for the navigation tree badges / hiding empty nodes). */
export const categoryCounts = (index: IndexItem[], treeIndex: TreeIndex): Record<string, number> => {
    const counts: Record<string, number> = {};
    for (const item of index) {
        if (item.type !== 'product') {
            continue;
        }
        // Dedupe per item so an article that reaches the same ancestor via two parallel
        // systems is only counted once for that ancestor.
        const touched = new Set<string>();
        for (const nodeId of item.nodeIds ?? []) {
            const entry = treeIndex.get(nodeId);
            if (!entry) {
                continue;
            }
            for (const id of [...entry.ancestorIds, nodeId]) {
                touched.add(id);
            }
        }
        touched.forEach((id) => {
            counts[id] = (counts[id] ?? 0) + 1;
        });
    }
    return counts;
};

// ---- document-mode tree navigation & usage rollup ------------------------------------
// A DocumentIndexItem row targets articles (artNrs, each resolved via indexByArtNr's own nodeIds -
// same as products) and/or tree system nodes directly (nodeIds - the converter's per-node
// assignments). One row per assetId, so BOTH lists can be populated on the same row, and both have
// to feed the same tree navigation/counts as products. The tree BADGES themselves live further
// down, next to the document search they have to agree with (see documentCategoryCounts).

/** True if a document row (article- or node-targeted) lies on or below the selected tree node. */
export const inDocumentCategory = (
    doc: DocumentIndexItem,
    nodeId: string | undefined,
    treeIndex: TreeIndex,
    indexByArtNr: Map<string, IndexItem>
): boolean => {
    if (!nodeId) {
        return true;
    }
    if (!treeIndex.has(nodeId)) {
        return false;
    }
    if ((doc.nodeIds ?? []).some((id) => isInSubtree(id, nodeId, treeIndex))) {
        return true;
    }
    // Deliberately not an `else`: now that rows are grouped per asset, one row can carry direct
    // system-node targets AND article targets at once, so a miss on the nodes still has to try
    // the articles.
    return (doc.artNrs ?? []).some((artNr) =>
        (indexByArtNr.get(artNr)?.nodeIds ?? []).some((id) => isInSubtree(id, nodeId, treeIndex))
    );
};

// The landing page's Gruppe/Familie/System multi-select (see state.scopeNodeIdsAtom): more than
// one chosen node is not a tree SELECTION (the results page navigates one node at a time) but a
// SCOPE - the catalog is cut down to the union of those subtrees before anything else (tree
// counts, facets, search, documents) looks at it. An empty list means no restriction.

/** True if the item lies on or below ANY of `nodeIds`; `[]` = no restriction. */
export const inAnyCategory = (item: IndexItem, nodeIds: string[], treeIndex: TreeIndex): boolean =>
    nodeIds.length === 0 || nodeIds.some((nodeId) => inCategory(item, nodeId, treeIndex));

/** Document counterpart of inAnyCategory (article- or node-targeted rows, see inDocumentCategory). */
export const inAnyDocumentCategory = (
    doc: DocumentIndexItem,
    nodeIds: string[],
    treeIndex: TreeIndex,
    indexByArtNr: Map<string, IndexItem>
): boolean => nodeIds.length === 0 || nodeIds.some((nodeId) => inDocumentCategory(doc, nodeId, treeIndex, indexByArtNr));

export interface DocumentUsageEntry {
    id: string;
    name: string;
}

export interface DocumentUsage {
    articles: DocumentUsageEntry[];
    nodes: DocumentUsageEntry[];
}

/**
 * Narrows each row's targets to one system: only the articles that belong to it, and the node
 * itself only when the document is assigned to it directly. Rows the system does not touch at all
 * are dropped.
 *
 * NEEDED BECAUSE ROWS ARE GROUPED PER ASSET. A row carries every target of its document, so
 * filtering a list down to "rows this system touches" keeps the whole document's usage on the row
 * - the out-of-system articles included. Before the rows were grouped, one row meant one target
 * and a plain filter did both jobs at once.
 *
 * Getting this wrong is not subtle: in the delivered sample, asset `3f0c2848-…` hangs on 91
 * articles, of which "Element-Deckenschalung Dokadek" holds exactly one. An unnarrowed row makes
 * its "Verwendung im System" column read 91 and link 90 articles that are not in the system -
 * the same class of defect as the 10-vs-61 mismatch that DocumentOverlayPage's systemUsage
 * comment records.
 */
export const scopeDocumentsToSystem = (
    documents: DocumentIndexItem[],
    nodeId: string,
    systemArticleNrs: Set<string>
): DocumentIndexItem[] => {
    const scoped: DocumentIndexItem[] = [];
    for (const doc of documents) {
        const artNrs = (doc.artNrs ?? []).filter((artNr) => systemArticleNrs.has(artNr));
        const nodeIds = (doc.nodeIds ?? []).includes(nodeId) ? [nodeId] : [];
        if (artNrs.length === 0 && nodeIds.length === 0) {
            continue;
        }
        scoped.push({ ...doc, artNrs, nodeIds });
    }
    return scoped;
};

/**
 * assetId -> the articles/nodes that directly reference it, resolved to display names - feeds the
 * "Verwendung" column's count + popover.
 *
 * Little more than a name lookup now: a row already IS its own usage entry (one row per assetId,
 * targets in artNrs/nodeIds). This used to walk every row and push into a shared entry behind an
 * `.some()` duplicate check - quadratic in the number of owners per asset, re-run on every filter
 * change, over a list that at 100,000 articles would have held ~429,000 rows.
 */
export const buildDocumentUsageIndex = (
    allDocuments: DocumentIndexItem[],
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex,
    locale: string
): Map<string, DocumentUsage> => {
    const usage = new Map<string, DocumentUsage>();
    for (const doc of allDocuments) {
        usage.set(doc.assetId, {
            articles: (doc.artNrs ?? []).map((id) => ({ id, name: indexByArtNr.get(id)?.name ?? id })),
            nodes: (doc.nodeIds ?? []).map((id) => ({ id, name: nodeLabel(treeIndex.get(id)?.node, locale) })),
        });
    }
    return usage;
};

// ---- "Did you mean ...?" (client-side, no backend) ----------------------------------

/**
 * Typo vocabulary per index, built on first use and kept for as long as that index array lives
 * (same lifetime logic as fieldTexts: a new delivery is a new array, the old vocabulary is
 * collected with it). Lazy on purpose - it is only ever needed after a search came up empty, so a
 * session that never mistypes never pays for it.
 */
const vocabularies = new WeakMap<IndexItem[], FuzzyVocabulary>();

const vocabularyFor = (index: IndexItem[], config: CatalogConfig): FuzzyVocabulary => {
    let vocab = vocabularies.get(index);
    if (!vocab) {
        vocab = new FuzzyVocabulary();
        // Only the free-text fields: those are what a corrected word will be searched in. Article
        // numbers and the like are matched by the exact stage and make poor "typo" targets.
        const keys = config.search.fields.filter((f) => f.pattern === 'contains').map((f) => f.key);
        for (const item of index) {
            if (!config.search.includeParts && item.type !== 'product') {
                continue;
            }
            for (const key of keys) {
                const raw = item[key];
                if (raw != null) {
                    vocab.addText(normalizedField(item, key), String(Array.isArray(raw) ? raw.join(' ') : raw));
                }
            }
        }
        vocabularies.set(index, vocab);
    }
    return vocab;
};

/**
 * Stage 3 of the search: a corrected query for one that found nothing, or undefined.
 *
 * Each word of the query that is not already part of the catalog's text is replaced by the closest
 * vocabulary word within the typo tolerance (see FuzzyVocabulary.correct). The suggestion is only
 * offered if it actually yields hits - a "Meinten Sie" that leads to an empty list is worse than
 * none. "Yields hits" is the CALLER's list, not the catalog: `hitCount` runs the candidate through
 * the pipeline that fills that list (tree node, column filters), which a catalog-wide check cannot
 * see - without it a node-scoped search offered corrections whose click left the list empty again.
 * It is deliberately a suggestion and not an automatic correction: product names and article
 * numbers are exactly where a silent "fix" would be wrong.
 */
export const suggestQuery = (
    index: IndexItem[],
    config: CatalogConfig,
    query: string,
    hitCount?: (candidate: string) => number
): string | undefined => {
    const words = splitWords(normalize(query));
    if (words.length === 0) {
        return undefined;
    }
    // The user's own spelling (umlauts and all) for the words that stay as they are.
    const typed = splitWords(query.toLowerCase());
    const vocab = vocabularyFor(index, config);
    let changed = false;
    const corrected = words.map((word, i) => {
        const folded = foldWord(word);
        if (vocab.knows(folded)) {
            return typed.length === words.length ? typed[i] : word;
        }
        const fix = vocab.correct(folded);
        if (fix === undefined) {
            return word;
        }
        changed = true;
        return fix;
    });
    if (!changed) {
        return undefined;
    }
    const suggestion = corrected.join(' ');
    const hits = hitCount ? hitCount(suggestion) : searchIndex(index, config, suggestion).length;
    return hits > 0 ? suggestion : undefined;
};

// ---- catalog-wide document search (documents.json) -----------------------------------

/** "PPH_product_data_sheet" -> "Product Data Sheet" - the raw STIBO taxonomy id has no label
 * table of its own. Lives here (not in a page component) so DocumentSearchResults and
 * ArticleDetailPage's MediaTab can share it without a React/DOM dependency. */
export const humanizeDocType = (raw: string): string =>
    raw
        .replace(/^PPH_/, '')
        .split('_')
        .filter(Boolean)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(' ');

/** ISO-ish code (e.g. "bg") -> localized language name ("Bulgarisch"/"Bulgarian"), falling back
 * to the bare code if the runtime has no data for it. */
// Same reasoning as the collator cache above: `new Intl.DisplayNames` is expensive, and this is
// called once per document row AND inside sort comparators.
const displayNames = new Map<string, Intl.DisplayNames | null>();

const displayNamesFor = (locale: string): Intl.DisplayNames | null => {
    if (!displayNames.has(locale)) {
        try {
            displayNames.set(locale, new Intl.DisplayNames([locale], { type: 'language' }));
        } catch {
            displayNames.set(locale, null);
        }
    }
    return displayNames.get(locale) ?? null;
};

export const languageLabel = (code: string, locale: string): string => {
    try {
        const label = displayNamesFor(locale)?.of(code);
        return label ? label[0].toUpperCase() + label.slice(1) : code.toUpperCase();
    } catch {
        return code.toUpperCase();
    }
};

/**
 * Distinct language/docType codes present in a document set, sorted by their TRANSLATED display
 * label (languageLabel/humanizeDocType) rather than the raw code - `[...new Set(...)].sort()`
 * alone sorts the raw codes, which put e.g. "cs" (Tschechisch) before "da" (Dänisch) before "sv"
 * (Schwedisch) - alphabetical by code, but scrambled by the label a user actually scans for (see
 * chat: "sv" looked "missing" because it sat far down a code-sorted list, past what fit before
 * scrolling, not because it was dropped or capped anywhere). Used by SearchMask's advanced filters
 * (landing page), which deliberately pass the catalog-wide `documents.json` array, since that is a
 * catalog-wide document search - memoizeByRef's cross-page-navigation caching applies to that
 * shared array. The document lists of a system, an article and the Merkliste build their options
 * with computeDocumentFacets over their OWN documents instead (components/DocumentFilterBar.tsx):
 * offering the catalog-wide set there showed languages/types the list did not have.
 *
 * Not built on memoizeByRef directly: locale is a second cache dimension a single WeakMap can't
 * key on, so this nests a plain Map per locale inside one WeakMap-per-array instead - same
 * "compute once, cache forever" shape, just one level deeper.
 */
const languageCache = new WeakMap<DocumentIndexItem[], Map<string, string[]>>();

export const distinctDocumentLanguages = (documents: DocumentIndexItem[], locale: string): string[] => {
    let byLocale = languageCache.get(documents);
    if (!byLocale) {
        byLocale = new Map();
        languageCache.set(documents, byLocale);
    }
    let result = byLocale.get(locale);
    if (!result) {
        result = [...new Set(documents.map((d) => d.language).filter((v): v is string => Boolean(v)))].sort((a, b) =>
            compareText(languageLabel(a, locale), languageLabel(b, locale), locale)
        );
        byLocale.set(locale, result);
    }
    return result;
};

/** Same as {@link distinctDocumentLanguages}, sorted by the humanized docType label instead - no
 * locale dimension, so this one IS a direct memoizeByRef. */
export const distinctDocumentTypes = memoizeByRef((documents: DocumentIndexItem[]): string[] =>
    [...new Set(documents.map((d) => d.docType).filter((v): v is string => Boolean(v)))].sort((a, b) =>
        compareText(humanizeDocType(a), humanizeDocType(b))
    )
);

export interface MediaGroup<T extends MediaItem> {
    /** A stable representative (first by language code) - callers wanting a specific language
     * pick it out of `variants` themselves (see DocumentSearchResults' per-row language switch). */
    primary: T;
    /** Every language variant of this same logical document (same `documentId`), sorted by
     * language code - length 1 when there is only one. */
    variants: T[];
}

/**
 * One variant per language, first one wins - what a language PICKER needs, as opposed to the full
 * variant list. Even after the assetId dedupe above, a delivery can carry two different assets for
 * the same language; a Select keyed by language must not offer that value twice (Mantine rejects
 * duplicate option values outright).
 */
export const languageVariants = <T extends MediaItem>(variants: T[]): T[] => {
    const byLanguage = new Map<string, T>();
    for (const variant of variants) {
        const key = variant.language ?? '';
        if (!byLanguage.has(key)) {
            byLanguage.set(key, variant);
        }
    }
    return [...byLanguage.values()];
};

/**
 * Collapses language variants of the same logical document (same `documentId` - see
 * MediaItem/Document_central) into one group each, instead of one row per language - the legacy
 * DOPI application's UX (a single row with a language switcher). Shared by ArticleDetailPage's
 * MediaTab (`MediaItem[]`) and the catalog-wide document search (`DocumentIndexItem[]`) - both
 * are already deduplicated/filtered by the caller before grouping.
 */
export const groupMediaByDocument = <T extends MediaItem>(items: T[]): MediaGroup<T>[] => {
    const groups = new Map<string, T[]>();
    const seenAssets = new Set<string>();
    for (const item of items) {
        // The SAME asset legitimately appears several times in documents.json - once per direct
        // target (an article row and a node row, see DocumentIndexItem). Grouping those raw rows
        // produced a "language variant" twice, which is what made the overlay's language Select
        // throw `[@mantine/core] Duplicate options are not supported` (see chat). The list path
        // deduped by assetId before calling this (filterDocumentGroups); doing it here covers
        // every caller instead of one.
        if (seenAssets.has(item.assetId)) {
            continue;
        }
        seenAssets.add(item.assetId);
        const key = item.documentId ?? item.assetId;
        const list = groups.get(key);
        if (list) {
            list.push(item);
        } else {
            groups.set(key, [item]);
        }
    }
    return [...groups.values()].map((variants) => {
        const sorted = [...variants].sort((a, b) => compareText(a.language ?? '', b.language ?? ''));
        return { primary: sorted[0], variants: sorted };
    });
};

/** Sortable columns of the document list - `title` is the default (see selectDocuments). */
export type DocumentSortKey = 'title' | 'language' | 'edition' | 'docType';

export interface DocumentSortState {
    key: DocumentSortKey;
    dir: 'asc' | 'desc';
}

export interface DocumentSearchState {
    search: string;
    language?: string;
    /** Multiple types may be selected at once (checkbox-style filter, matching the legacy DOPI
     * application's "Dokumentfilter" panel) - empty/absent means no restriction. */
    docTypes?: string[];
    /** Edition dates ("MM/YYYY" strings, straight from MediaItem.editionDate) - multi-select like
     * docTypes; empty/absent means no restriction. A row with NO editionDate matches only when the
     * filter is inactive (same convention as the docType filter one line up). */
    editions?: string[];
    nodeId?: string;
    /** Column sort; defaults to title ascending, which is how the list has always been ordered. */
    sort?: DocumentSortState;
    /** UI locale - the list resolves each row to THAT language's variant (see
     * DocumentSearchResults' useResolvedDocument), so the sort has to look at the same one. */
    locale?: string;
    page: number;
    perPage: number;
}

export interface DocumentSearchResult {
    /** One entry per LOGICAL document (language variants collapsed, see groupMediaByDocument) -
     * pagination counts these groups, not raw rows, so a document with many language variants
     * doesn't crowd out a whole page by itself. */
    items: MediaGroup<DocumentIndexItem>[];
    total: number;
}

/**
 * Single entry point for the catalog-wide document search (mirrors selectCatalog's role for
 * products). Filters by language/docType/tree node, matches the keyword against the document
 * title AND the owning article's product name (via indexByArtNr - the same map already built
 * for buildDocumentUsageIndex), sorts by title, groups language variants of the same document
 * into one row each (see groupMediaByDocument), and paginates.
 *
 * No deduplication step: documents.json carries one row per assetId already (see
 * DocumentIndexItem), with every target of that file on the row itself.
 */
const documentRowMatches = (
    d: DocumentIndexItem,
    state: Pick<DocumentSearchState, 'language' | 'docTypes' | 'editions' | 'nodeId'>,
    q: string,
    searching: boolean,
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex
): boolean => {
    if (state.language && d.language !== state.language) {
        return false;
    }
    if (state.docTypes && state.docTypes.length > 0 && (!d.docType || !state.docTypes.includes(d.docType))) {
        return false;
    }
    if (state.editions && state.editions.length > 0 && (!d.editionDate || !state.editions.includes(d.editionDate))) {
        return false;
    }
    if (!inDocumentCategory(d, state.nodeId, treeIndex, indexByArtNr)) {
        return false;
    }
    if (!searching) {
        return true;
    }
    // Every owning article of this file, not just one: the row now carries all of them, and a
    // document has to stay findable by ANY article it hangs on.
    const artNrs = d.artNrs ?? [];
    const productNames = artNrs.map((artNr) => indexByArtNr.get(artNr)?.name ?? '').join(' ');
    if (normalize(`${d.descr ?? ''} ${artNrs.join(' ')} ${productNames}`).includes(q)) {
        return true;
    }
    // The document's own ids, for a full-UUID query only (same reasoning as ID_FIELDS above): the
    // assetId is the blob name under assets/, so a UUID from a URL or a failing request finds its
    // document; documentId groups the language variants of one logical document; nodeIds are the
    // tree systems the file is attached to, which makes a node UUID find products (via ID_FIELDS'
    // nodeIds) and its documents alike. Note these are the row's DIRECT targets only - narrowing a
    // list to a whole subtree is what the category filter does (see inDocumentCategory), not the
    // search box.
    return (
        isTechnicalIdQuery(q) &&
        normalize(`${d.assetId} ${d.documentId ?? ''} ${(d.nodeIds ?? []).join(' ')}`).includes(q)
    );
};

/** Filter rows per documentRowMatches, then collapse language variants into logical documents.
 * Extracted so selectDocuments (the result list) and computeDocumentFacets (its facet counts) can
 * never drift apart on what counts as one result.
 *
 * The assetId dedupe this used to do first is gone: documents.json delivers one row per asset. */
const filterDocumentGroups = (
    documents: DocumentIndexItem[],
    state: Pick<DocumentSearchState, 'language' | 'docTypes' | 'editions' | 'nodeId'>,
    q: string,
    searching: boolean,
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex
): MediaGroup<DocumentIndexItem>[] =>
    groupMediaByDocument(
        documents.filter((d) => documentRowMatches(d, state, q, searching, indexByArtNr, treeIndex))
    );

/**
 * The variant a row actually SHOWS: the UI locale's, else the group's representative - the exact
 * rule DocumentSearchResults' useResolvedDocument applies per row. Sorting has to use the same one,
 * otherwise the list is ordered by values the user cannot see (see chat: a row titled
 * "DokaXdek-Tisch" sat at the very top of an A-Z sort, because its REPRESENTATIVE carried no title
 * and the old code fell back to the assetId - a UUID, which sorts before any letter).
 */
const shownVariant = (group: MediaGroup<DocumentIndexItem>, locale?: string): DocumentIndexItem =>
    group.variants.find((variant) => (variant.language ?? '') === (locale ?? '')) ?? group.primary;

/**
 * Sorts the collapsed document groups by one column, on the values the table displays.
 *
 * Edition dates go through editionSortKey (a "MM/YYYY" string sorts wrong as text); language sorts
 * by its raw code rather than its translated label, which for the delivered set (de/en/fr/…) is the
 * same order either way. A missing value sorts as an empty string - never as an assetId.
 */
const sortDocumentGroups = (
    groups: MediaGroup<DocumentIndexItem>[],
    sort?: DocumentSortState,
    locale?: string
): MediaGroup<DocumentIndexItem>[] => {
    const factor = sort?.dir === 'desc' ? -1 : 1;
    const key = sort?.key ?? 'title';
    return [...groups].sort((a, b) => {
        if (key === 'edition') {
            const diff =
                editionSortKey(shownVariant(a, locale).editionDate ?? '') -
                editionSortKey(shownVariant(b, locale).editionDate ?? '');
            return diff * factor;
        }
        const valueOf = (group: MediaGroup<DocumentIndexItem>): string => {
            const shown = shownVariant(group, locale);
            if (key === 'language') {
                return shown.language ?? '';
            }
            if (key === 'docType') {
                return shown.docType ? humanizeDocType(shown.docType) : '';
            }
            return shown.descr ?? '';
        };
        return compareText(valueOf(a), valueOf(b)) * factor;
    });
};

/**
 * Distinct LOGICAL document counts per tree node id - the document-mode navigation badges (and the
 * "hide empty branches" rule in NavigationTree). Counted in exactly the unit the result list shows
 * as "N Treffer", so a badge can never contradict what selecting that node delivers:
 *
 * - Language variants of one document (same documentId) collapse into ONE count, the same way
 *   groupMediaByDocument collapses them into one row. Counting raw assetIds instead is what made a
 *   node advertise far more documents than its list then showed (see chat: "Träger XT20" badge 62
 *   vs 23 Treffer; "DokaCAD" badge 5 vs 2 Treffer - 5 files, but a de/en/sk and a de/en document).
 * - The active document filters (language/docType/edition) and the running search apply here too -
 *   mirroring the product tree, whose counts run on the facet-filtered catalog (see CatalogPage).
 *
 * A row reaches EVERY branch it is attached to: its own direct system nodes plus the nodes of each
 * owning article. That used to need one row per attachment; the row now carries all of them.
 */
export const documentCategoryCounts = (
    documents: DocumentIndexItem[],
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex,
    minChars: number,
    state: Pick<DocumentSearchState, 'search' | 'language' | 'docTypes' | 'editions'>
): Record<string, number> => {
    const q = normalize(state.search);
    const searching = q.length >= minChars;
    const counts: Record<string, number> = {};
    const seenByNode = new Map<string, Set<string>>(); // nodeId -> logical document keys counted there
    for (const doc of documents) {
        // nodeId deliberately left out: the rollup below places each row in its own branch, this
        // only applies the filters/search the list applies (see filterDocumentGroups).
        if (!documentRowMatches(doc, { ...state, nodeId: undefined }, q, searching, indexByArtNr, treeIndex)) {
            continue;
        }
        // One row per asset, so the group key needs no per-asset memo: language variants of one
        // logical document are separate assets that share a documentId, which is exactly what
        // collapses them into a single count here.
        const key = doc.documentId ?? doc.assetId;
        const ownNodeIds = [
            ...(doc.nodeIds ?? []),
            ...(doc.artNrs ?? []).flatMap((artNr) => indexByArtNr.get(artNr)?.nodeIds ?? []),
        ];
        const touched = new Set<string>();
        for (const id of ownNodeIds) {
            const entry = treeIndex.get(id);
            if (!entry) {
                continue;
            }
            for (const ancestorId of [...entry.ancestorIds, id]) {
                touched.add(ancestorId);
            }
        }
        touched.forEach((id) => {
            let seen = seenByNode.get(id);
            if (!seen) {
                seenByNode.set(id, (seen = new Set()));
            }
            if (!seen.has(key)) {
                seen.add(key);
                counts[id] = (counts[id] ?? 0) + 1;
            }
        });
    }
    return counts;
};

export const selectDocuments = (
    documents: DocumentIndexItem[],
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex,
    minChars: number,
    state: DocumentSearchState
): DocumentSearchResult => {
    const q = normalize(state.search);
    const searching = q.length >= minChars;
    const groups = filterDocumentGroups(documents, state, q, searching, indexByArtNr, treeIndex);
    const sorted = sortDocumentGroups(groups, state.sort, state.locale);
    const total = sorted.length;
    // Clamp the page so a stale, too-high page never slices an empty window after a filter shrinks
    // the result set - same fix as selectCatalog's safePage.
    const pageCount = Math.max(1, Math.ceil(total / state.perPage));
    const safePage = Math.min(Math.max(1, state.page), pageCount);
    const start = (safePage - 1) * state.perPage;
    return { items: sorted.slice(start, start + state.perPage), total };
};

export interface DocumentFacetEntry {
    value: string;
    /** Logical documents (groups, same unit as DocumentSearchResult.total) this value would yield
     * under the OTHER groups' active filters - 0 means "selecting this now gives no results", the
     * UI grays it out rather than hiding it. */
    count: number;
}

export interface DocumentFacets {
    languages: DocumentFacetEntry[];
    docTypes: DocumentFacetEntry[];
    editions: DocumentFacetEntry[];
}

/** "MM/YYYY" sorted newest-first; anything unparseable sorts after the parseable block, by plain
 * string compare, so a malformed delivery value stays visible instead of crashing the sort. */
const editionSortKey = (value: string): number => {
    const match = /^(\d{2})\/(\d{4})$/.exec(value);
    return match ? (Number(match[2]) * 12) + Number(match[1]) : -1;
};

/**
 * Facet values + counts for the document search's filter panel (DocumentFacetsPanel), following
 * the same hard-won rules as the product side (see doka-pih skill, principle 3, and the earlier
 * SPA bug it recounts):
 *
 * - The VALUE LIST of each group comes from the base set (search + tree-node applied, NO facet
 *   filters) - so picking a filter never makes another group's options vanish.
 * - Each group's COUNTS are computed with the OTHER groups' filters applied but its own excluded
 *   ("smart graying") - e.g. picking a language grays out (count 0) exactly those docTypes that
 *   have no document in that language, while the language list itself stays counted against
 *   docType/edition only. Counts count logical documents (groups), the same unit the result
 *   list's own Treffer total uses.
 *
 * A group of language variants counts toward EVERY language (and edition) present in it - matching
 * selectDocuments, where filtering rows by language keeps the group (with just that variant).
 */
export const computeDocumentFacets = (
    documents: DocumentIndexItem[],
    indexByArtNr: Map<string, IndexItem>,
    treeIndex: TreeIndex,
    minChars: number,
    state: DocumentSearchState,
    locale: string
): DocumentFacets => {
    const q = normalize(state.search);
    const searching = q.length >= minChars;

    const tally = (
        groups: MediaGroup<DocumentIndexItem>[],
        pick: (d: DocumentIndexItem) => string | null | undefined
    ): Map<string, number> => {
        const counts = new Map<string, number>();
        for (const group of groups) {
            const values = new Set(group.variants.map(pick).filter((v): v is string => Boolean(v)));
            for (const value of values) {
                counts.set(value, (counts.get(value) ?? 0) + 1);
            }
        }
        return counts;
    };

    const groupsFor = (overrides: Partial<DocumentSearchState>) =>
        filterDocumentGroups(documents, { ...state, ...overrides }, q, searching, indexByArtNr, treeIndex);

    // Base set: value lists (all values that exist under search + node, ungrayed or not).
    const base = groupsFor({ language: undefined, docTypes: undefined, editions: undefined });
    // Per-group sets with the group's OWN filter excluded: counts.
    const languageCounts = tally(groupsFor({ language: undefined }), (d) => d.language);
    const docTypeCounts = tally(groupsFor({ docTypes: undefined }), (d) => d.docType);
    const editionCounts = tally(groupsFor({ editions: undefined }), (d) => d.editionDate);

    const entries = (
        pick: (d: DocumentIndexItem) => string | null | undefined,
        counts: Map<string, number>,
        sort: (a: string, b: string) => number
    ): DocumentFacetEntry[] =>
        [...tally(base, pick).keys()]
            .sort(sort)
            .map((value) => ({ value, count: counts.get(value) ?? 0 }));

    return {
        languages: entries(
            (d) => d.language,
            languageCounts,
            // Sorted by TRANSLATED display label, not raw code - same reasoning as
            // distinctDocumentLanguages above ("sv" mid-list under S, not near the end).
            (a, b) => compareText(languageLabel(a, locale), languageLabel(b, locale), locale)
        ),
        docTypes: entries((d) => d.docType, docTypeCounts, (a, b) => compareText(humanizeDocType(a), humanizeDocType(b))),
        editions: entries((d) => d.editionDate, editionCounts, (a, b) => {
            const diff = editionSortKey(b) - editionSortKey(a);
            return diff !== 0 ? diff : compareText(a, b);
        }),
    };
};

// ---- article attribute formatting / comparison ---------------------------------------
// Shared by ArticleDetailPage's attribute tab (one article) and the Merkliste's compare table
// (several articles side by side, see components/CompareModal.tsx).

// UNECE Recommendation 20 unit codes, as STIBO delivers them raw on a measurement attribute (e.g.
// "unece.unit.KGM") - translated to a short, human-readable unit for display (was showing the raw
// code as-is, e.g. "540.000 unece.unit.KGM" instead of "540.000 kg" - see chat screenshot). A
// fixed, well-known standard (not project-/data-model-specific like attribute names or LOV codes),
// so - unlike labels.lovs below - this lives as a static per-locale table right here instead of
// coming from labels.<lang>.json. Only the units actually seen in real deliveries/sample data plus
// a handful of very common ones are covered; an unmapped code falls back to the untouched raw
// value (see unitLabel) - same "never hide the value" rule formatAttributeValue already applies to
// an unmapped LOV code below.
const UNIT_LABELS: Record<string, { de: string; en: string }> = {
    KGM: { de: 'kg', en: 'kg' },
    GRM: { de: 'g', en: 'g' },
    TNE: { de: 't', en: 't' },
    MTR: { de: 'm', en: 'm' },
    MMT: { de: 'mm', en: 'mm' },
    CMT: { de: 'cm', en: 'cm' },
    KTM: { de: 'km', en: 'km' },
    MTK: { de: 'm²', en: 'm²' },
    MMK: { de: 'mm²', en: 'mm²' },
    CMK: { de: 'cm²', en: 'cm²' },
    MTQ: { de: 'm³', en: 'm³' },
    LTR: { de: 'l', en: 'l' },
    ANN: { de: 'Jahre', en: 'years' },
    MON: { de: 'Monate', en: 'months' },
    WEE: { de: 'Wochen', en: 'weeks' },
    DAY: { de: 'Tage', en: 'days' },
    HUR: { de: 'Stunden', en: 'hours' },
    MIN: { de: 'Minuten', en: 'minutes' },
    SEC: { de: 'Sekunden', en: 'seconds' },
    DEG: { de: '°', en: '°' },
    CEL: { de: '°C', en: '°C' },
    P1: { de: '%', en: '%' },
};

/**
 * "unece.unit.KGM" (or a bare "KGM") -> "kg" (de/en alike here, but the table carries both since
 * several codes - Jahre/years, Tage/days, ... - genuinely differ). Falls back to `rawUnit`
 * UNCHANGED (not even the "unece.unit." prefix stripped) when the code isn't in UNIT_LABELS - the
 * same default as before this table existed, kept deliberately so a still-unmapped unit stays
 * exactly as recognizable/greppable as it always was, not silently truncated.
 */
export const unitLabel = (rawUnit: string, locale: string): string => {
    const code = rawUnit.replace(/^unece\.unit\./, '');
    const entry = UNIT_LABELS[code];
    if (!entry) {
        return rawUnit;
    }
    return locale === 'en' ? entry.en : entry.de;
};

/**
 * Renders one raw attribute value per attribute-mapping.json's valueType. LOV codes are
 * translated via labels.lovs[attrId] regardless of the declared valueType ('simple' or 'lov') -
 * attribute-mapping.json is hand-maintained separately from the converter's data model and some
 * real LOV-bound attributes (e.g. AT_lifecycle_group/AT_resp_subsidiary) are classified "simple"
 * there; falling back to the raw value when no translation exists covers both correctly-typed and
 * mistyped attributes without depending on that classification.
 */
/** Clear text of a value-list facet value from labels.<lang>.json's LOV tables ("AllCountries" ->
 * "Alle Länder"), falling back to the raw code. The one lookup FacetsPanel, SearchMask's selects
 * and its preset caption all share - a facet's LOV table is keyed by its attrId (or its key when it
 * has none, e.g. objectTypeId). */
export const lovLabel = (labels: CatalogLabels, facet: Pick<FacetDef, 'key' | 'attrId'>, value: string): string =>
    labels.lovs[facet.attrId ?? facet.key]?.[value] ?? value;

/** Display order of a value-list facet's values (FacetDef.sort/firstValues): pinned codes first,
 * in their configured order, then either the incoming (count) order or the LOV clear text
 * alphabetically. Used by FacetsPanel and SearchMask alike so drawer and landing page agree. */
export const orderFacetValues = <T extends { value: string }>(
    entries: T[],
    facet: Pick<FacetDef, 'key' | 'attrId' | 'sort' | 'firstValues'>,
    labels: CatalogLabels,
    locale: string
): T[] => {
    const first = facet.firstValues ?? [];
    if (first.length === 0 && facet.sort !== 'label') {
        return entries;
    }
    const rank = (value: string) => {
        const index = first.indexOf(value);
        return index === -1 ? first.length : index;
    };
    return [...entries].sort((a, b) => {
        const byPin = rank(a.value) - rank(b.value);
        if (byPin !== 0) {
            return byPin;
        }
        return facet.sort === 'label' ? compareText(lovLabel(labels, facet, a.value), lovLabel(labels, facet, b.value), locale) : 0;
    });
};

export const formatAttributeValue = (
    attrId: string,
    raw: unknown,
    valueType: AttributeValueType,
    locale: string,
    labels: CatalogLabels
): string => {
    if (raw == null) {
        return '–';
    }
    if (valueType === 'localized' && typeof raw === 'object') {
        return loc(raw as Record<string, string>, locale, '');
    }
    if (valueType === 'measurement' && typeof raw === 'object') {
        const measurement = raw as { unit?: string; value?: unknown };
        const unit = measurement.unit ? unitLabel(measurement.unit, locale) : measurement.unit;
        return [measurement.value, unit].filter((part) => part != null && part !== '').join(' ');
    }
    if (Array.isArray(raw)) {
        return raw.map((v) => labels.lovs[attrId]?.[String(v)] ?? String(v)).join(', ');
    }
    if (typeof raw === 'boolean') {
        return raw ? '✓' : '–';
    }
    return labels.lovs[attrId]?.[String(raw)] ?? String(raw);
};

// Sentinel so entries without an explicit order sort after every ranked one, rather than before
// (Number.MIN would put unranked/legacy entries first, which reads as "someone forgot to rank
// this" more clearly at the end than jumbled in at the start).
const UNRANKED = Number.MAX_SAFE_INTEGER;

export interface AttributeCompareRow {
    attrId: string;
    label: string;
    order?: number;
    /** One formatted value per input article, same order/length as the `articles` argument. */
    values: string[];
}

export interface AttributeCompareGroup {
    key: string;
    label: string;
    groupOrder?: number;
    rows: AttributeCompareRow[];
}

/**
 * Groups raw attributes by attribute-mapping.json's `group`, ordered by `groupOrder` (customer's
 * "Kategorie V1" sequence) and, within a group, by `order` (customer's "Sort." column) - both from
 * the customer's Datenkategorisierung spreadsheet. Entries missing either field sort after all
 * ranked ones, by label, instead of crashing or defaulting to 0.
 *
 * Takes an ARRAY of articles' attribute maps rather than one: ArticleDetailPage's single-article
 * tab calls this with a one-element array (see groupAttributes there), while the Merkliste compare
 * table (components/CompareModal.tsx) passes up to three side by side. The row set is the UNION of
 * attribute ids present on any of them, so an attribute only one article carries still gets its
 * own row (others show '–' via formatAttributeValue's raw==null branch) instead of the row being
 * silently dropped.
 */
export const buildAttributeCompareGroups = (
    articles: { attributes: Record<string, unknown> }[],
    attributeMapping: AttributeMapping,
    locale: string,
    labels: CatalogLabels
): AttributeCompareGroup[] => {
    const attrIds = new Set<string>();
    articles.forEach((a) => Object.keys(a.attributes).forEach((id) => attrIds.add(id)));

    const groups = new Map<string, AttributeCompareGroup>();
    for (const attrId of attrIds) {
        const meta = attributeMapping[attrId];
        if (!meta) {
            // No presentation metadata shipped for this attribute id - nothing to render it with.
            continue;
        }
        const groupKey = meta.group.de ?? meta.group.en ?? attrId;
        const group =
            groups.get(groupKey) ??
            { key: groupKey, label: loc(meta.group, locale, groupKey), groupOrder: meta.groupOrder, rows: [] };
        // Keep the lowest groupOrder seen for this group, in case entries disagree.
        if (meta.groupOrder != null && (group.groupOrder == null || meta.groupOrder < group.groupOrder)) {
            group.groupOrder = meta.groupOrder;
        }
        group.rows.push({
            attrId,
            label: loc(meta.label, locale, attrId),
            order: meta.order,
            values: articles.map((a) => formatAttributeValue(attrId, a.attributes[attrId], meta.valueType, locale, labels)),
        });
        groups.set(groupKey, group);
    }
    for (const group of groups.values()) {
        group.rows.sort(
            (a, b) => (a.order ?? UNRANKED) - (b.order ?? UNRANKED) || compareText(a.label, b.label, locale)
        );
    }
    return [...groups.values()].sort(
        (a, b) => (a.groupOrder ?? UNRANKED) - (b.groupOrder ?? UNRANKED) || compareText(a.label, b.label, locale)
    );
};
